/**
 * What the tracker chip, the case pane's live row and the sidebar read from
 * the running tracker. `useWorkTracker` writes the snapshot; the chip writes
 * `paused` and `override` (switch case).
 */
import { create } from "zustand";

import type { TrackerContext } from "../types";

type State = {
  /** The context being counted (the route's, or the chip's override). */
  context: TrackerContext | null;
  /** Seconds counted on `context`'s case in this tab today. */
  seconds: number;
  counting: boolean;
  paused: boolean;
  /** Set from the chip's "Switch case"; cleared when the route changes case. */
  override: TrackerContext | null;
  labels: Record<string, string>;
  /** The user's own agent turns streaming in this tab; watching one is work. */
  agentTurns: number;
  /** Count a turn while it streams; call the returned function when it ends. */
  startAgentTurn: () => () => void;
  setSnapshot: (snap: {
    context: TrackerContext | null;
    seconds: number;
    counting: boolean;
  }) => void;
  setPaused: (paused: boolean) => void;
  setOverride: (ctx: TrackerContext | null) => void;
  addLabels: (labels: Record<string, string> | undefined) => void;
};

export const useTrackerStore = create<State>((set) => ({
  context: null,
  seconds: 0,
  counting: false,
  paused: false,
  override: null,
  labels: {},
  agentTurns: 0,
  startAgentTurn: () => {
    set((s) => ({ agentTurns: s.agentTurns + 1 }));
    let ended = false;
    return () => {
      if (ended) return;
      ended = true;
      set((s) => ({ agentTurns: Math.max(0, s.agentTurns - 1) }));
    };
  },
  setSnapshot: (snap) =>
    set((s) =>
      s.context === snap.context &&
      s.seconds === snap.seconds &&
      s.counting === snap.counting
        ? s
        : { ...s, ...snap }
    ),
  setPaused: (paused) => set({ paused }),
  setOverride: (override) => set({ override }),
  addLabels: (labels) =>
    labels && Object.keys(labels).length
      ? set((s) => ({ labels: { ...s.labels, ...labels } }))
      : undefined
}));

export const labelFor = (
  labels: Record<string, string>,
  ctx: TrackerContext | null
): string | null => {
  if (!ctx) return null;
  return (
    (ctx.individual_id && labels[ctx.individual_id]) ||
    (ctx.foreign_national_id && labels[ctx.foreign_national_id]) ||
    null
  );
};
